import { useMemo, useState } from "react";
import { AlertCircle, Check, Search, Wallet } from "lucide-react";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { createIdempotencyScope } from "@/core/api/idempotency";
import {
  MutationConfirmPreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import { useVerifyNonMemberCode } from "@/features/wallet/api/use-verify-non-member-code";
import { useRedeemNonMemberCode } from "@/features/wallet/api/use-redeem-non-member-code";
import type { NonMemberCodeVerifyResponse } from "@/features/wallet/api/dto";

interface Props {
  /** `agentId` + `agentPartyId` de la session agent (fournis par la page). */
  agentId: string;
  agentPartyId: string;
}

/**
 * Formulaire agent Non-Member (guide v2.4 §6.3) — étape 1 vérification
 * (`POST /NonMemberCodes/verify`, sans effet financier) puis étape 2
 * encaissement sous preview (`POST /NonMemberCodes/redeem`, idempotence
 * OBLIGATOIRE §4.7).
 *
 * Anti double-encaissement par verrou optimiste côté serveur (422 au second).
 * 5 échecs de vérification de téléphone bloquent définitivement le code.
 */
export function NonMemberRedeemForm({ agentId, agentPartyId }: Props) {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;

  const verify = useVerifyNonMemberCode();
  const redeem = useRedeemNonMemberCode();

  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [verified, setVerified] = useState<NonMemberCodeVerifyResponse | null>(
    null
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  const scope = useMemo(() => createIdempotencyScope(), []);

  const handleVerify = () => {
    if (!code || !phone) return;
    verify.mutate(
      { code: code.trim().toUpperCase(), phone: phone.trim() },
      {
        onSuccess: (data) => setVerified(data),
        onError: (error) => {
          setVerified(null);
          notifSilenceError(apiErrorHandler(error));
        },
      }
    );
  };

  const handleRedeem = () => {
    if (!verified) return;
    redeem.mutate(
      {
        body: {
          code: verified.code,
          phone: phone.trim(),
          agentId,
          agentPartyId,
        },
        idempotencyKey: scope.key,
      },
      {
        onSuccess: () => {
          notifSilence(tx("wallet.nonMember.feedback.redeemed"));
          scope.renew(); // Nouvelle tentative = nouvelle clé.
          setCode("");
          setPhone("");
          setVerified(null);
          setPreviewOpen(false);
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      }
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-5 w-5 text-primary" />
            {tx("wallet.nonMember.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {tx("wallet.nonMember.description")}
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="nmc-code">{tx("wallet.nonMember.form.code")}</Label>
              <Input
                id="nmc-code"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.toUpperCase().replace(/\s/g, ""))
                }
                placeholder="FP-AB3D5F7H9K2M"
                className="font-mono tracking-wider"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="nmc-phone">{tx("wallet.nonMember.form.phone")}</Label>
              <Input
                id="nmc-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+224620000000"
                inputMode="tel"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleVerify}
              disabled={!code || !phone || verify.isPending}
            >
              {verify.isPending ? (
                <ButtonSpinner />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              {tx("wallet.nonMember.form.verify")}
            </Button>
            {verified ? (
              <p className="text-xs text-success flex items-center gap-1">
                <Check className="h-3.5 w-3.5" />
                {tx("wallet.nonMember.form.verified")}
              </p>
            ) : null}
          </div>

          {verified ? (
            <div className="rounded-md border border-success/40 bg-success-muted p-3 flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs uppercase text-muted-foreground">
                  {tx("wallet.nonMember.result.amount")}
                </span>
                <NoTranslate as="span" className="text-lg font-semibold">
                  {formatMinorAmount(verified.amountMinor, verified.currency)}
                </NoTranslate>
              </div>
              {verified.recipientDisplayName ? (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {tx("wallet.nonMember.result.recipient")}
                  </span>
                  <NoTranslate as="span" className="font-medium">
                    {verified.recipientDisplayName}
                  </NoTranslate>
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground">
                  {tx("wallet.nonMember.result.expiresAt")}
                </span>
                <NoTranslate as="span" className="font-mono">
                  {verified.expiresAt}
                </NoTranslate>
              </div>
              <Button
                className="mt-2"
                onClick={() => setPreviewOpen(true)}
                disabled={redeem.isPending}
              >
                {redeem.isPending ? <ButtonSpinner /> : null}
                {tx("wallet.nonMember.form.redeem")}
              </Button>
            </div>
          ) : null}

          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
            {tx("wallet.nonMember.warning")}
          </p>
        </CardContent>
      </Card>

      <MutationConfirmPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        data={
          verified
            ? {
                code: verified.code,
                phone,
                amountMinor: verified.amountMinor,
                currency: verified.currency,
                agentId,
              }
            : {}
        }
        title={tx("wallet.nonMember.preview.title")}
        description={tx("wallet.nonMember.preview.description")}
        confirmLabel={tx("wallet.nonMember.form.redeem")}
        pending={redeem.isPending}
        onConfirm={handleRedeem}
      />
    </>
  );
}
